import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { getSetupDetails, updateSetupDetails } from "../../store/AsyncMethods/SetupMethod";
import { toast } from "react-toastify";

// Categorized frontend routes
const FRONTEND_ROUTES = [
  {
    label: "Main Pages",
    items: [
      { label: "Home", value: "/" },
      { label: "Shop", value: "/shop" },
      { label: "Shops", value: "/shops" },
      { label: "Contact", value: "/contact" },
      { label: "Offers", value: "/offers" },
    ]
  },
  {
    label: "Information Pages",
    items: [
      { label: "FAQs", value: "/faqs" },
      { label: "Terms & Conditions", value: "/terms-conditions" },
      { label: "Refund Policy", value: "/refund-policy" },
    ]
  },
  {
    label: "User Account",
    items: [
      { label: "Login", value: "/login" },
      { label: "Register", value: "/register" },
      { label: "My Profile", value: "/my-profile" },
      { label: "My Orders", value: "/my-orders" },
    ]
  },
  {
    label: "Shopping",
    items: [
      { label: "Checkout", value: "/checkout" },
      { label: "Empty Cart", value: "/empty-cart" },
    ]
  },
  {
    label: "Custom URL",
    items: [
      { label: "Enter Custom URL...", value: "custom" },
    ]
  }
];

function MenuBuilder() {
  const dispatch = useDispatch();
  const { setupDetails } = useSelector((state) => state.SetupReducer);
  const [menu, setMenu] = useState([]);
  const [customUrlInputs, setCustomUrlInputs] = useState({});
  

  useEffect(() => {
    dispatch(getSetupDetails());
  }, [dispatch]);

  useEffect(() => {
    if (setupDetails && setupDetails.menu) {
      try {
        setMenu(Array.isArray(setupDetails.menu) ? setupDetails.menu : JSON.parse(setupDetails.menu));
      } catch (e) {
        setMenu([]);
      }
    } else {
      setMenu([]);
    }
    // load existing header color
    
  }, [setupDetails]);

  

  const addTopItem = () => {
    const item = { id: Date.now(), title: "New Item", type: "link", url: "/", children: [] };
    setMenu((m) => [...m, item]);
  };

  

  // --- Recursive helpers to manipulate nested menu structure ---
  const updateItemById = (items, id, data) => {
    return (items || []).map((it) => {
      if (it.id === id) return { ...it, ...data };
      if (it.children && it.children.length) return { ...it, children: updateItemById(it.children, id, data) };
      return it;
    });
  };

  const removeItemById = (items, id) => {
    const res = [];
    for (const it of (items || [])) {
      if (it.id === id) continue;
      if (it.children && it.children.length) {
        res.push({ ...it, children: removeItemById(it.children, id) });
      } else {
        res.push(it);
      }
    }
    return res;
  };

  const addChildToParent = (items, parentId, child) => {
    if (parentId == null) {
      const arr = Array.from(items || []);
      arr.push(child);
      return arr;
    }
    return (items || []).map((it) => {
      if (it.id === parentId) {
        const ch = Array.isArray(it.children) ? Array.from(it.children) : [];
        ch.push(child);
        return { ...it, children: ch };
      }
      if (it.children && it.children.length) {
        return { ...it, children: addChildToParent(it.children, parentId, child) };
      }
      return it;
    });
  };

  const removeFromParent = (items, parentId, index) => {
    // returns { newItems, removed }
    if (parentId == null) {
      const arr = Array.from(items || []);
      const [removed] = arr.splice(index, 1);
      return { newItems: arr, removed };
    }
    let removed = null;
    const helper = (list) => (list || []).map((it) => {
      if (it.id === parentId) {
        const ch = Array.isArray(it.children) ? Array.from(it.children) : [];
        removed = ch.splice(index, 1)[0];
        return { ...it, children: ch };
      }
      if (it.children && it.children.length) return { ...it, children: helper(it.children) };
      return it;
    });
    const newItems = helper(items || []);
    return { newItems, removed };
  };

  const insertToParent = (items, parentId, index, node) => {
    if (parentId == null) {
      const arr = Array.from(items || []);
      arr.splice(index, 0, node);
      return arr;
    }
    const helper = (list) => (list || []).map((it) => {
      if (it.id === parentId) {
        const ch = Array.isArray(it.children) ? Array.from(it.children) : [];
        ch.splice(index, 0, node);
        return { ...it, children: ch };
      }
      if (it.children && it.children.length) return { ...it, children: helper(it.children) };
      return it;
    });
    return helper(items || []);
  };

  const removeTopItem = React.useCallback((id) => {
    setMenu((m) => removeItemById(m, id));
  }, []);

  const addSubItem = React.useCallback((parentId) => {
    const child = { id: Date.now(), title: "New Subitem", type: "link", url: "/", children: [] };
    setMenu((m) => addChildToParent(m, parentId, child));
  }, []);

  const updateItem = React.useCallback((id, data) => {
    setMenu((m) => updateItemById(m, id, data));
  }, []);

  const removeSubItem = React.useCallback((parentId, id) => {
    setMenu((m) => removeItemById(m, id));
  }, []);

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const { source, destination } = result;

    const parentIdFrom = (droppableId) => {
      if (!droppableId || !droppableId.startsWith("sub-")) return null;
      return parseInt(droppableId.replace("sub-", ""), 10);
    };

    const srcParent = parentIdFrom(source.droppableId);
    const dstParent = parentIdFrom(destination.droppableId);

    // same parent reorder
    if (srcParent === dstParent) {
      if (srcParent == null) {
        const arr = Array.from(menu);
        const [moved] = arr.splice(source.index, 1);
        arr.splice(destination.index, 0, moved);
        setMenu(arr);
        return;
      }
      // reorder within same parent's children (remove then insert adjusting dest)
      const { newItems: afterRemoval, removed } = removeFromParent(menu, srcParent, source.index);
      if (!removed) return;
      const destIndex = source.index < destination.index ? destination.index - 1 : destination.index;
      const inserted = insertToParent(afterRemoval, dstParent, destIndex, removed);
      setMenu(inserted);
      return;
    }

    // moving between different parents (or top <-> sub)
    const { newItems: afterRemoval, removed } = removeFromParent(menu, srcParent, source.index);
    if (!removed) return;
    const inserted = insertToParent(afterRemoval, dstParent, destination.index, removed);
    setMenu(inserted);
  };

  const save = async () => {
    try {
      const formData = new FormData();
      formData.append("menu", JSON.stringify(menu));
      const success = await dispatch(updateSetupDetails(formData));
      if (success) {
        toast.success("Menu saved");
        dispatch(getSetupDetails());
      }
    } catch (e) {
      toast.error("Save failed");
    }
  };

  // Handle URL selection from dropdown
  const handleUrlChange = React.useCallback((itemId, selectedValue) => {
    if (selectedValue === "custom") {
      setCustomUrlInputs(prev => ({ ...prev, [itemId]: true }));
    } else {
      setCustomUrlInputs(prev => {
        const updated = { ...prev };
        delete updated[itemId];
        return updated;
      });
      updateItem(itemId, { url: selectedValue });
    }
  }, [updateItem]);

  // Define RenderItems outside of render to prevent re-creation on each render
  const RenderItems = React.useCallback(({ items, parentId }) => (
    <Droppable droppableId={parentId == null ? "top" : `sub-${parentId}`} type={parentId == null ? "TOP" : `SUB-${parentId}`}>
      {(provided) => (
        <div ref={provided.innerRef} {...provided.droppableProps}>
          {(items || []).map((item, idx) => {
            const isCustomUrl = customUrlInputs[item.id] ||
              (item.url && !FRONTEND_ROUTES.some(group =>
                group.items.some(route => route.value === item.url)
              ));

            return (
              <Draggable key={item.id} draggableId={String(item.id)} index={idx}>
                {(prov) => (
                  <div ref={prov.innerRef} {...prov.draggableProps} className="mb-3 bg-white p-3 border rounded">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span {...prov.dragHandleProps} className="cursor-move pi pi-bars mr-2" />
                        <InputText
                          value={item.title}
                          onChange={(e) => updateItem(item.id, { title: e.target.value })}
                          className="w-64"
                          placeholder="Menu Title"
                        />
                        {isCustomUrl ? (
                          <div className="flex items-center gap-2">
                            <InputText
                              value={item.url}
                              onChange={(e) => updateItem(item.id, { url: e.target.value })}
                              className="w-64"
                              placeholder="/path or https://..."
                            />
                            <Button
                              icon="pi pi-list"
                              className="p-button-text p-button-sm"
                              onClick={() => {
                                setCustomUrlInputs(prev => {
                                  const updated = { ...prev };
                                  delete updated[item.id];
                                  return updated;
                                });
                              }}
                              tooltip="Switch to dropdown"
                            />
                          </div>
                        ) : (
                          <Dropdown
                            value={item.url}
                            options={FRONTEND_ROUTES}
                            optionLabel="label"
                            optionGroupLabel="label"
                            optionGroupChildren="items"
                            onChange={(e) => handleUrlChange(item.id, e.value)}
                            className="w-64 ml-2"
                            placeholder="Select Route..."
                            filter
                          />
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button icon="pi pi-plus" className="p-button-text" onClick={() => addSubItem(item.id)} />
                        <Button icon="pi pi-trash" className="p-button-text p-button-danger" onClick={() => removeTopItem(item.id)} />
                      </div>
                    </div>

                    <div className="mt-3 pl-6">
                      <RenderItems items={item.children || []} parentId={item.id} />
                    </div>
                  </div>
                )}
              </Draggable>
            );
          })}
          {provided.placeholder}
        </div>
      )}
    </Droppable>
  ), [updateItem, addSubItem, removeTopItem, customUrlInputs, handleUrlChange]);

  return (
    <div className="mx-4">
      <div className="card shadow-md rounded p-4 mt-3">
        <h2 className="text-xl font-semibold mb-3">Menu Builder (drag to reorder)</h2>
        <div className="mb-3 flex flex-col md:flex-row md:items-center md:gap-4">
          <div className="flex items-center gap-3">
            <Button label="Add Menu Item" icon="pi pi-plus" onClick={addTopItem} />
          </div>
        </div>



        <DragDropContext onDragEnd={onDragEnd}>
          <RenderItems items={menu} parentId={null} />
        </DragDropContext>

        <div className="flex justify-end mt-4">
          <Button label="Save Menu" icon="pi pi-save" onClick={save} />
        </div>
      </div>
    </div>
  );
}

export default MenuBuilder;
